import * as React from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { addMonths, format, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  CalendarBlank,
  CaretLeft,
  CaretRight,
  DownloadSimple,
  Export,
} from 'phosphor-react';
import { DateRange, DayPicker } from 'react-day-picker';
import { useNavigate } from 'react-router-dom';
import 'react-day-picker/style.css';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import { IEmpresaOption } from '../../../../../hooks/useContaCilImportar';
import {
  IDiaExportavel,
  IExportarResult,
  useContaCilDiasExportaveis,
  useContaCilExportar,
} from '../../../../../hooks/useContaCilLancamentos';
import { useContaCilContas } from '../../../../../hooks/useContaCilContasBancarias';
import { useContaCilRegrasBancos } from '../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { EmpresaPicker } from '../components/EmpresaPicker';
import { useAplicarEmpresaParam } from '../useEmpresaParam';
import { propsMenuSelect } from '../../../../../utils/selectMenu';

const C = {
  teal: '#0F766E',
  tealDark: '#115E59',
  tealLight: '#CCFBF1',
  ink: '#0F172A',
  muted: '#64748B',
  subtle: '#94A3B8',
  amberBg: '#FEF3C7',
  redBg: '#FECACA',
};

type Formato = 'dominio' | 'csv';

const campoLabel = (children: React.ReactNode) => (
  <Text fontSize="10px" fontWeight="600" color={C.muted} mb={1} textTransform="uppercase">
    {children}
  </Text>
);

const ymKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const isoDate = (date: Date) => format(date, 'yyyy-MM-dd');

/** Calendario heatmap em popover: dias coloridos por status_agg, range conciliado. */
function PeriodoPickerButton({
  empresaId,
  banco,
  conta,
  value,
  onChange,
  isDisabled,
}: {
  empresaId?: number;
  banco?: string | null;
  conta?: string | null;
  value: DateRange | undefined;
  onChange: (r: DateRange | undefined) => void;
  isDisabled?: boolean;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [mesVisivel, setMesVisivel] = React.useState<Date>(new Date());
  const anoMes = ymKey(mesVisivel);

  const {
    data: resposta,
    isLoading,
    isPlaceholderData,
  } = useContaCilDiasExportaveis(empresaId, anoMes, banco, conta);
  // Enquanto o mes novo carrega, `resposta` ainda e' a do mes anterior: vale pra
  // lista de meses (setas), mas nao pros dias do mes visivel.
  const data = isPlaceholderData ? undefined : resposta;
  const mesesDisponiveis = resposta?.meses_disponiveis;

  // Mapa data ISO -> info do dia, pra lookup O(1) nos modifiers/snap.
  const diasMap = React.useMemo(() => {
    const m = new Map<string, IDiaExportavel>();
    if (!data) return m;
    data.dias.forEach(d => {
      const iso = `${data.ano_mes}-${String(d.dia).padStart(2, '0')}`;
      m.set(iso, d);
    });
    return m;
  }, [data]);

  // Quando os meses_disponiveis carregam, ja' posiciona no mes mais recente
  // que tem dado (uma vez por carregamento de empresa).
  React.useEffect(() => {
    if (!mesesDisponiveis?.length) return;
    const ultimo = mesesDisponiveis[mesesDisponiveis.length - 1];
    if (ultimo !== anoMes && !value?.from) {
      const [a, m] = ultimo.split('-').map(Number);
      setMesVisivel(new Date(a, m - 1, 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesesDisponiveis?.join('|')]);

  // Snap: se o usuario selecionou range que cruza dia nao-verde, trunca no
  // ultimo verde contiguo a' partir do `from`. Toast explicativo.
  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange(undefined);
      return;
    }
    if (!range.to || range.to.getTime() === range.from.getTime()) {
      onChange({ from: range.from, to: range.to ?? range.from });
      return;
    }
    const start = range.from < range.to ? range.from : range.to;
    const end = range.from < range.to ? range.to : range.from;
    const cursor = new Date(start);
    let ultimoValido = new Date(start);
    while (cursor.getTime() <= end.getTime()) {
      // Pendente/revisão (amarelo/vermelho) corta o range. Dia vazio (sem
      // lançamento) NÃO corta — exporta por cima do buraco (1→5 mesmo com o 4
      // vazio); o arquivo só inclui os dias com conciliado de qualquer jeito.
      const agg = diasMap.get(isoDate(cursor))?.status_agg ?? 'vazio';
      if (agg === 'amarelo' || agg === 'vermelho') break;
      ultimoValido = new Date(cursor);
      cursor.setDate(cursor.getDate() + 1);
    }
    const truncou = ultimoValido.getTime() !== end.getTime();
    if (truncou) {
      toast({
        status: 'info',
        title: 'Range ajustado',
        description: `Tem dia com pendente/em revisão no meio — final virou ${format(ultimoValido, 'dd/MM')}.`,
        duration: 3500,
      });
    }
    onChange({ from: start, to: ultimoValido });
  };

  // Setas '< >' navegam mesmo entre meses sem dado (deixa o user procurar),
  // mas ficam desabilitadas quando nao ha' nada antes/depois.
  const meses = mesesDisponiveis ?? [];
  const podeIrAntes = meses.some(ym => ym < anoMes);
  const podeIrDepois = meses.some(ym => ym > anoMes);
  // Trava visual em vez de `isDisabled`: desabilitar a seta que acabou de ser
  // clicada (ao chegar no primeiro/ultimo mes) tira o foco dela e o Popover
  // (closeOnBlur) fecha.
  const travaSeta = (pode: boolean) => ({
    'aria-disabled': !pode,
    opacity: pode ? 1 : 0.4,
    cursor: pode ? 'pointer' : 'not-allowed',
    _hover: pode ? undefined : { bg: 'transparent' },
  });

  const totalConciliadoRange = React.useMemo(() => {
    if (!value?.from || !value?.to || !data) return 0;
    // Data LOCAL (não new Date('YYYY-MM-DD'), que parseia UTC e desloca -3h BRT,
    // desalinhando da comparação com value.from/to que vêm locais do DayPicker).
    const [yy, mm] = data.ano_mes.split('-').map(Number);
    let total = 0;
    data.dias.forEach(d => {
      const dt = new Date(yy, mm - 1, d.dia);
      if (dt >= (value.from as Date) && dt <= (value.to as Date)) {
        total += d.qtd_conciliado;
      }
    });
    return total;
  }, [value, data]);

  // Dias do mês visível com pendente/revisão (amarelo/vermelho) — aviso de que o
  // mês não está 100% conciliado (esses dias não entram no arquivo).
  const diasPendentesMes = React.useMemo(
    () =>
      (data?.dias ?? []).filter(d => d.status_agg === 'amarelo' || d.status_agg === 'vermelho')
        .length,
    [data],
  );

  const labelBotao = (() => {
    if (!value?.from) return 'Selecione o período';
    const fmtFrom = format(value.from, 'dd/MM');
    if (!value.to) return `${fmtFrom} — ...`;
    const fmtTo = format(value.to, 'dd/MM/yyyy');
    const dias =
      Math.round((value.to.getTime() - value.from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return `${fmtFrom} → ${fmtTo}  ·  ${dias} dia${dias > 1 ? 's' : ''}`;
  })();

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start" closeOnBlur>
      <PopoverTrigger>
        <Button
          variant="outline"
          isDisabled={isDisabled}
          leftIcon={<CalendarBlank size={16} weight="bold" />}
          onClick={onOpen}
          borderRadius="lg"
          h="40px"
          w="full"
          justifyContent="flex-start"
          fontWeight="500"
          fontSize="13px"
          bg="white"
          color={value?.from ? C.ink : C.subtle}
        >
          {labelBotao}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="auto"
          minW="320px"
          borderRadius="xl"
          boxShadow="0 12px 32px -8px rgba(15,23,42,0.18)"
          zIndex={1500}
        >
          <PopoverArrow />
          <PopoverBody p={3}>
            <Flex justify="space-between" align="center" mb={2} px={1}>
              <IconButton
                aria-label="Mês anterior"
                icon={<CaretLeft size={14} weight="bold" />}
                size="xs"
                variant="ghost"
                {...travaSeta(podeIrAntes)}
                onClick={() => podeIrAntes && setMesVisivel(subMonths(mesVisivel, 1))}
              />
              <Flex align="center" gap={2}>
                <Text fontWeight="600" fontSize="13px" color={C.ink} textTransform="capitalize">
                  {format(mesVisivel, 'MMMM/yyyy', { locale: ptBR })}
                </Text>
                {(isLoading || isPlaceholderData) && <Spinner size="xs" color={C.teal} />}
              </Flex>
              <IconButton
                aria-label="Próximo mês"
                icon={<CaretRight size={14} weight="bold" />}
                size="xs"
                variant="ghost"
                {...travaSeta(podeIrDepois)}
                onClick={() => podeIrDepois && setMesVisivel(addMonths(mesVisivel, 1))}
              />
            </Flex>

            <Box
              className="contacil-calendar"
              sx={{
                '& .rdp-root': {
                  '--rdp-accent-color': C.teal,
                  '--rdp-accent-background-color': C.tealLight,
                  '--rdp-day-width': '36px',
                  '--rdp-day-height': '34px',
                  '--rdp-day_button-width': '32px',
                  '--rdp-day_button-height': '30px',
                  '--rdp-day_button-border-radius': '8px',
                  '--rdp-selected-border': '0',
                  '--rdp-today-color': C.teal,
                  '--rdp-disabled-opacity': '1',
                  margin: 0,
                  padding: 0,
                },
                '& .rdp-month_caption, & .rdp-nav': { display: 'none' },
                '& .rdp-month_grid': { borderCollapse: 'separate', borderSpacing: '2px' },
                '& .rdp-weekday': {
                  fontSize: '10px',
                  fontWeight: 600,
                  color: C.subtle,
                  textTransform: 'uppercase',
                },
                '& .rdp-day': {
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: 0,
                },
                '& .rdp-day_button': {
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: 'transparent',
                },
                // Dias clicaveis (verde = 100% conciliado)
                '& .rdp-day.day-verde .rdp-day_button': {
                  backgroundColor: 'rgba(15, 118, 110, 0.10)',
                  color: C.tealDark,
                  fontWeight: 600,
                },
                '& .rdp-day.day-verde:hover .rdp-day_button': {
                  backgroundColor: 'rgba(15, 118, 110, 0.18)',
                },
                // Dias visiveis mas nao clicaveis: amarelo / vermelho / vazio
                '& .rdp-day.day-amarelo .rdp-day_button': {
                  backgroundColor: C.amberBg,
                  color: '#92400E',
                  cursor: 'not-allowed',
                },
                '& .rdp-day.day-vermelho .rdp-day_button': {
                  backgroundColor: C.redBg,
                  color: '#991B1B',
                  cursor: 'not-allowed',
                },
                '& .rdp-day.day-vazio .rdp-day_button': {
                  color: '#CBD5E1',
                  cursor: 'not-allowed',
                },
                // Range selecionado — override pra ficar acima das cores acima
                '& .rdp-selected .rdp-day_button, & .rdp-range_start .rdp-day_button, & .rdp-range_end .rdp-day_button':
                  {
                    backgroundColor: `${C.teal} !important`,
                    color: 'white !important',
                    fontWeight: 700,
                  },
                '& .rdp-range_middle .rdp-day_button': {
                  backgroundColor: `${C.tealLight} !important`,
                  color: `${C.tealDark} !important`,
                  borderRadius: '0 !important',
                },
                '& .rdp-outside': { opacity: 0.4 },
              }}
            >
              <DayPicker
                mode="range"
                selected={value}
                onSelect={handleSelect}
                month={mesVisivel}
                onMonthChange={setMesVisivel}
                locale={ptBR}
                showOutsideDays
                disabled={(date: Date) => {
                  if (ymKey(date) !== anoMes) return true;
                  // Só pendente/revisão bloqueia a seleção. Dia vazio (sem
                  // lançamento) é selecionável — permite marcar um range que
                  // atravessa o buraco (ex.: 1→5 com o dia 4 vazio).
                  const agg = diasMap.get(isoDate(date))?.status_agg ?? 'vazio';
                  return agg === 'amarelo' || agg === 'vermelho';
                }}
                modifiers={{
                  verde: (date: Date) =>
                    ymKey(date) === anoMes && diasMap.get(isoDate(date))?.status_agg === 'verde',
                  amarelo: (date: Date) =>
                    ymKey(date) === anoMes && diasMap.get(isoDate(date))?.status_agg === 'amarelo',
                  vermelho: (date: Date) =>
                    ymKey(date) === anoMes && diasMap.get(isoDate(date))?.status_agg === 'vermelho',
                  vazio: (date: Date) =>
                    ymKey(date) === anoMes &&
                    (diasMap.get(isoDate(date))?.status_agg ?? 'vazio') === 'vazio',
                }}
                modifiersClassNames={{
                  verde: 'day-verde',
                  amarelo: 'day-amarelo',
                  vermelho: 'day-vermelho',
                  vazio: 'day-vazio',
                }}
              />
            </Box>

            <Flex gap={3} mt={2} fontSize="10px" color={C.muted} justify="center" wrap="wrap">
              <Flex align="center" gap={1}>
                <Box w="10px" h="10px" bg={C.teal} borderRadius="sm" />
                <Text>conciliado</Text>
              </Flex>
              <Flex align="center" gap={1}>
                <Box
                  w="10px"
                  h="10px"
                  bg={C.amberBg}
                  borderRadius="sm"
                  border="1px solid #FCD34D"
                />
                <Text>parcial</Text>
              </Flex>
              <Flex align="center" gap={1}>
                <Box w="10px" h="10px" bg={C.redBg} borderRadius="sm" border="1px solid #F87171" />
                <Text>pendente/revisão</Text>
              </Flex>
            </Flex>

            {diasPendentesMes > 0 && (
              <Text fontSize="10px" color="#B45309" fontWeight="600" textAlign="center" mt={1}>
                {diasPendentesMes} dia(s) com pendente/revisão neste mês — não entram no arquivo;
                conclua a conciliação.
              </Text>
            )}

            {value?.from && value?.to && (
              <Box mt={2} p={2} bg="rgba(15, 118, 110, 0.06)" borderRadius="md">
                <Text fontSize="11px" color={C.tealDark} fontWeight="500">
                  {totalConciliadoRange} lançamento(s) conciliado(s) no período
                </Text>
              </Box>
            )}

            <Flex justify="space-between" mt={3} gap={2}>
              <Button
                size="sm"
                variant="ghost"
                color={C.muted}
                onClick={() => {
                  onChange(undefined);
                }}
              >
                Limpar
              </Button>
              <Button
                size="sm"
                bg={C.teal}
                color="white"
                _hover={{ bg: C.tealDark }}
                isDisabled={!value?.from || !value?.to}
                onClick={onClose}
              >
                Confirmar
              </Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

export function ConciliacaoContabilExportarPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [empresa, setEmpresa] = React.useState<IEmpresaOption | null>(null);
  useAplicarEmpresaParam(setEmpresa);
  const [formato, setFormato] = React.useState<Formato>('dominio');
  const [range, setRange] = React.useState<DateRange | undefined>(undefined);
  // Multi-seleção: vazio = todos. Vai pro backend como lista separada por
  // vírgula (mesma convenção do filtro de status em Extratos).
  const [bancosSel, setBancosSel] = React.useState<string[]>([]);
  const [contasSel, setContasSel] = React.useState<string[]>([]);
  const [resultado, setResultado] = React.useState<IExportarResult | null>(null);

  const bancoParam = bancosSel.length ? bancosSel.join(',') : undefined;
  const contaParam = contasSel.length ? contasSel.join(',') : undefined;

  const { data: bancos = [] } = useContaCilRegrasBancos({
    empresaId: empresa?.value,
    emUso: true,
  });
  const bancoOptions = React.useMemo(
    () => bancos.map(b => ({ value: b.nome, label: b.nome })),
    [bancos],
  );

  // As contas vêm recortadas pelos bancos escolhidos — quem casa o vocabulário
  // é o servidor. Sem banco escolhido, lista as contas da empresa inteira.
  const { data: contas = [] } = useContaCilContas(empresa?.value, bancoParam);
  const contaOptions = React.useMemo(
    () =>
      contas
        .filter(c => c.conta)
        .map(c => ({
          value: c.conta,
          // Com mais de um banco em jogo, "1234-5" sozinho fica ambíguo.
          label:
            bancosSel.length === 1
              ? `${c.conta}${c.agencia ? ` · ag. ${c.agencia}` : ''}`
              : `${c.banco_nome} · ${c.conta}`,
        })),
    [contas, bancosSel],
  );

  const exportar = useContaCilExportar();

  const gerar = async () => {
    if (!empresa || !range?.from || !range?.to) return;
    try {
      const r = await exportar.mutateAsync({
        empresaId: empresa.value,
        formato,
        dateInit: isoDate(range.from),
        dateEnd: isoDate(range.to),
        banco: bancoParam,
        conta: contaParam,
      });
      setResultado(r);
      if (r.total === 0) {
        toast({
          status: 'info',
          title: 'Nada para exportar',
          description: 'Nenhum lançamento com débito e crédito no filtro.',
        });
      }
    } catch (err) {
      toast({
        status: 'error',
        title: 'Não foi possível exportar',
        description: extractApiErrorMessage(err, 'Falha ao gerar o arquivo. Tente novamente.'),
        duration: 9000,
      });
    }
  };

  const baixar = () => {
    if (!resultado) return;
    const csv = resultado.formato === 'csv';
    // O Domínio (desktop Windows) lê o .txt como ANSI/Windows-1252 — gravar em
    // UTF-8 vira mojibake ("APLICAÇÃO" -> "APLICAÃ‡ÃƒO"). Grava o Domínio em
    // Latin-1 (acentos PT batem 1:1; fora do Latin-1 vira '?'). CSV fica UTF-8.
    let corpo: BlobPart = resultado.conteudo;
    if (!csv) {
      const src = resultado.conteudo;
      const bytes = new Uint8Array(src.length);
      for (let i = 0; i < src.length; i += 1) {
        const code = src.charCodeAt(i);
        bytes[i] = code <= 0xff ? code : 0x3f; // 0x3f = '?'
      }
      corpo = bytes;
    }
    const blob = new Blob([corpo], {
      type: csv ? 'text/plain;charset=utf-8' : 'text/plain;charset=windows-1252',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      resultado.formato === 'csv'
        ? resultado.filename.replace(/\.txt$/, '.csv')
        : resultado.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const trocarEmpresa = (opt: IEmpresaOption | null) => {
    setEmpresa(opt);
    setResultado(null);
    setBancosSel([]);
    setContasSel([]);
    setRange(undefined);
  };

  React.useEffect(() => {
    // Banco mudou → o conjunto de dias verdes muda. Limpa pra evitar range
    // "fantasma" da seleção anterior.
    setRange(undefined);
  }, [bancoParam]);

  React.useEffect(() => {
    // Conta mudou → outro conjunto de dias exportáveis.
    setRange(undefined);
  }, [contaParam]);

  React.useEffect(() => {
    // Descarta conta escolhida que deixou de pertencer aos bancos filtrados.
    // Podar é melhor que zerar tudo: numa multi-seleção, limpar as contas a
    // cada ajuste de banco faria o contador refazer o trabalho. E deixar a
    // conta órfã seria pior — ela filtraria um recorte vazio em silêncio, que
    // é o sintoma que esta tela acabou de parar de ter.
    if (!contasSel.length) return;
    const validas = new Set(contaOptions.map(o => o.value));
    const podadas = contasSel.filter(c => validas.has(c));
    if (podadas.length !== contasSel.length) setContasSel(podadas);
  }, [contaOptions, contasSel]);

  const botaoFormato = (val: Formato, label: string) => (
    <Button
      size="sm"
      flex={1}
      borderRadius="lg"
      variant="unstyled"
      h="36px"
      fontWeight="600"
      fontSize="13px"
      bg={formato === val ? 'white' : 'transparent'}
      color={formato === val ? C.teal : C.muted}
      boxShadow={formato === val ? '0 2px 8px -2px rgba(15,23,42,0.12)' : undefined}
      onClick={() => setFormato(val)}
    >
      {label}
    </Button>
  );

  return (
    <GlassPageBg>
      <Box maxW="1024px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={5}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color={C.muted}
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: C.ink }}
              onClick={() => navigate('/modulesHome/accountingConciliation/conciliacao-contabil')}
            />
            <Flex
              w="52px"
              h="52px"
              borderRadius="2xl"
              align="center"
              justify="center"
              color="white"
              bg={C.teal}
              boxShadow="0 8px 24px -4px rgba(15,118,110,0.4)"
            >
              <Export size={28} weight="fill" />
            </Flex>
            <Box>
              <Text
                fontSize="11px"
                color={C.subtle}
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Conciliação Contábil
              </Text>
              <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color={C.ink}>
                Exportar Domínio
              </Heading>
            </Box>
          </Flex>
        </motion.div>

        <GlassCard intensity="medium" p={5} mb={5}>
          <Box mb={4}>
            <EmpresaPicker value={empresa} onChange={trocarEmpresa} />
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box>
              {campoLabel('Formato')}
              <Flex
                bg="rgba(15,23,42,0.04)"
                borderRadius="xl"
                p="3px"
                gap="2px"
                borderWidth="1px"
                borderColor="rgba(15,23,42,0.06)"
              >
                {botaoFormato('dominio', 'Domínio (.txt)')}
                {botaoFormato('csv', 'CSV')}
              </Flex>
            </Box>
            <Box>
              {campoLabel('Período')}
              <PeriodoPickerButton
                empresaId={empresa?.value}
                banco={bancoParam}
                conta={contaParam}
                value={range}
                onChange={setRange}
                isDisabled={!empresa}
              />
            </Box>
          </SimpleGrid>

          <Flex mt={4} gap={3} align="flex-end" wrap="wrap">
            <Box flex={1} minW="220px">
              {campoLabel('Bancos (opcional)')}
              <RSelect
                isMulti
                size="sm"
                options={bancoOptions}
                value={bancoOptions.filter(o => bancosSel.includes(o.value))}
                onChange={opts =>
                  setBancosSel(((opts ?? []) as readonly { value: string }[]).map(o => o.value))
                }
                placeholder="Todos os bancos"
                closeMenuOnSelect={false}
                isDisabled={!empresa}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                {...propsMenuSelect}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Box flex={1} minW="220px">
              {campoLabel('Contas (opcional)')}
              <RSelect
                isMulti
                size="sm"
                options={contaOptions}
                value={contaOptions.filter(o => contasSel.includes(o.value))}
                onChange={opts =>
                  setContasSel(((opts ?? []) as readonly { value: string }[]).map(o => o.value))
                }
                placeholder="Todas as contas"
                closeMenuOnSelect={false}
                isDisabled={!empresa}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                {...propsMenuSelect}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Button
              leftIcon={<Export size={16} weight="fill" />}
              borderRadius="lg"
              px={5}
              color="white"
              fontWeight="600"
              bg={C.teal}
              _hover={{ filter: 'brightness(1.08)' }}
              isDisabled={!empresa || !range?.from || !range?.to}
              isLoading={exportar.isPending}
              loadingText="Gerando"
              onClick={gerar}
            >
              Gerar arquivo
            </Button>
          </Flex>
          <Text fontSize="11px" color={C.muted} mt={3}>
            Exporta só lançamentos com débito <b>e</b> crédito (partida dobrada completa).
            Transferência interna entra uma vez. Selecione apenas dias 100% conciliados.
          </Text>
        </GlassCard>

        {resultado && resultado.total > 0 && (
          <GlassCard intensity="medium" p={5}>
            <Flex align="center" justify="space-between" gap={3} mb={3} wrap="wrap">
              <Box>
                <Text fontSize="15px" fontWeight="700" color={C.ink}>
                  {resultado.total} lançamento(s) prontos
                </Text>
                {resultado.transferencias_dedup > 0 && (
                  <Text fontSize="11px" color={C.muted}>
                    {resultado.transferencias_dedup} transferência(s) interna(s) deduplicada(s)
                  </Text>
                )}
              </Box>
              <Button
                leftIcon={<DownloadSimple size={16} weight="bold" />}
                borderRadius="lg"
                px={5}
                color="white"
                fontWeight="600"
                bg={C.teal}
                _hover={{ filter: 'brightness(1.08)' }}
                onClick={baixar}
              >
                Baixar {resultado.formato === 'csv' ? '.csv' : '.txt'}
              </Button>
            </Flex>
            <Box
              as="pre"
              fontSize="11px"
              fontFamily="mono"
              color="#334155"
              bg="rgba(15,23,42,0.03)"
              borderRadius="lg"
              p={3}
              maxH="320px"
              overflow="auto"
              whiteSpace="pre"
            >
              {resultado.conteudo.slice(0, 4000)}
              {resultado.conteudo.length > 4000 ? '\n…' : ''}
            </Box>
          </GlassCard>
        )}
      </Box>
    </GlassPageBg>
  );
}
